// VisionScreeningForm.tsx
import React, { useEffect, useState } from "react";
import api from "../../../api/axiosConfig";
import uploadIcon from "../../../assets/icons/upload.svg";
import "../../../center admin css/centerAdmin.css";
import "../../../center admin css/VisionScreeningForm.css";

// ⭐ ADDED — S3 uploader hook
import { useS3Uploader } from "../../../hooks/useS3Uploader";

interface FormComponentProps {
  emrId: string;
  onComplete?: () => void;
}

type YesNo = "" | "yes" | "no";
type Outcome = "pass" | "fail" | "";
type EyeSide = "OD (Right Eye)" | "OS (Left Eye)";

type ObservExam = {
  findings: string[];

  leftEye: {
    result: YesNo;
    selected: string[];
  };

  rightEye: {
    result: YesNo;
    selected: string[];
  };
};
type DxNames =
  | "Hyperopia"
  | "Myopia"
  | "Anisometropia"
  | "Astigmatism"
  | "Strabismus"
  | "Anisocoria";

export const VisionScreeningForm: React.FC<FormComponentProps> = ({
  emrId,
  onComplete,
}) => {
  // ⭐ ADDED — local URLs after uploading to S3
  const [uploadedUrls, setUploadedUrls] = useState<string[]>([]);

  // ⭐ ADDED — initialize uploader
  const { uploadPdf, uploading, progress } = useS3Uploader(emrId);

  const [expandedSections, setExpandedSections] = useState({
    observational: true,
    photoScreener: true,
    diagnoses: true,
    pdfUpload: true,
    recommendation: true,
  });

  const toggleSection = (section: keyof typeof expandedSections) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  const [redReflex, setRedReflex] = useState<ObservExam>({
    findings: ["Absent", "White", "Dull", "Opacified", "Asymmetric"],

    leftEye: {
      result: "",
      selected: [],
    },

    rightEye: {
      result: "",
      selected: [],
    },
  });

  const [externalInspection, setExternalInspection] = useState<ObservExam>({
    // findings: ["Structural Abnormality (Ex: Ptosis, Epiphora)"],
    findings: ["Ptosis", "Epiphora"],

    leftEye: {
      result: "",
      selected: [],
    },

    rightEye: {
      result: "",
      selected: [],
    },
  });

  const [pupillaryExam, setPupillaryExam] = useState<ObservExam>({
    findings: [
      "Irregular Shape",
      "Unequal Size",
      "Poor Reaction to Light",
      "Unequal Reaction to Light",
    ],

    leftEye: {
      result: "",
      selected: [],
    },

    rightEye: {
      result: "",
      selected: [],
    },
  });

  const [fixFollow, setFixFollow] = useState<ObservExam>({
    findings: ["Failure to Fix", "Failure to Follow"],

    leftEye: {
      result: "",
      selected: [],
    },

    rightEye: {
      result: "",
      selected: [],
    },
  });

  const [cornealLightReflex, setCornealLightReflex] = useState<ObservExam>({
    findings: ["Asymmetric", "Displaced"],

    leftEye: {
      result: "",
      selected: [],
    },

    rightEye: {
      result: "",
      selected: [],
    },
  });

  const [coverTest, setCoverTest] = useState<ObservExam>({
    findings: ["Refixation Movement Present"],

    leftEye: {
      result: "",
      selected: [],
    },

    rightEye: {
      result: "",
      selected: [],
    },
  });

  const [ps, setPs] = useState<Record<string, Record<EyeSide, string>>>({
    "Refraction – DS": { "OD (Right Eye)": "", "OS (Left Eye)": "" },
    "Refraction – DC": { "OD (Right Eye)": "", "OS (Left Eye)": "" },
    Axis: { "OD (Right Eye)": "", "OS (Left Eye)": "" },
    "Spherical Equivalent": { "OD (Right Eye)": "", "OS (Left Eye)": "" },
    "Gaze Symmetry": { "OD (Right Eye)": "", "OS (Left Eye)": "" },
    "Pupil Diameter": { "OD (Right Eye)": "", "OS (Left Eye)": "" },
    "Pupillary Distance": { "OD (Right Eye)": "", "OS (Left Eye)": "" },
  });

  const [dx, setDx] = useState<Record<DxNames, Record<EyeSide, Outcome>>>({
    Hyperopia: { "OD (Right Eye)": "", "OS (Left Eye)": "" },
    Myopia: { "OD (Right Eye)": "", "OS (Left Eye)": "" },
    Anisometropia: { "OD (Right Eye)": "", "OS (Left Eye)": "" },
    Astigmatism: { "OD (Right Eye)": "", "OS (Left Eye)": "" },
    Strabismus: { "OD (Right Eye)": "", "OS (Left Eye)": "" },
    Anisocoria: { "OD (Right Eye)": "", "OS (Left Eye)": "" },
  });

  const [report, setReport] = useState<File | null>(null);
  const [recommendation, setRecommendation] = useState<"" | "Pass" | "Refer">(
    "",
  );

  const [finalRecommendation, setFinalRecommendation] = useState("");

  // ⭐ UPDATED — integrates S3 upload
  const onPickReport = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0] || null;
    if (!f) return;

    if (f.size > 10 * 1024 * 1024) {
      alert("Maximum file size is 10MB");
      return;
    }

    setReport(f);

    console.log("📤 Uploading Vision PDF to S3…");

    const uploaded = await uploadPdf(f, "vision-screening");

    if (uploaded?.key) {
      setUploadedUrls([uploaded.key]); // ONLY 1 PDF allowed in vision
      console.log("✅ Vision PDF uploaded:", uploaded.key);
    }
  };

  const removeReport = () => {
    setReport(null);
    setUploadedUrls([]);

    const input = document.getElementById(
      "reportUpload",
    ) as HTMLInputElement | null;

    if (input) {
      input.value = "";
    }
  };

  const setPsField =
    (key: string, eye: EyeSide) => (e: React.ChangeEvent<HTMLInputElement>) => {
      const val = e.target.value;
      setPs((prev) => ({
        ...prev,
        [key]: { ...prev[key], [eye]: val },
      }));
    };

  const setDxField = (name: DxNames, eye: EyeSide, value: Outcome) => () =>
    setDx((d) => ({
      ...d,
      [name]: { ...d[name], [eye]: value },
    }));

  const resetForm = () => {
    setRedReflex({
      findings: ["Absent", "White", "Dull", "Opacified", "Asymmetric"],
      leftEye: {
        result: "",
        selected: [],
      },
      rightEye: {
        result: "",
        selected: [],
      },
    });

    setExternalInspection({
      // findings: ["Structural Abnormality (Ex: Ptosis, Epiphora)"],
      findings: ["Ptosis", "Epiphora"],

      leftEye: {
        result: "",
        selected: [],
      },
      rightEye: {
        result: "",
        selected: [],
      },
    });

    setPupillaryExam({
      findings: [
        "Irregular Shape",
        "Unequal Size",
        "Poor Reaction to Light",
        "Unequal Reaction to Light",
      ],
      leftEye: {
        result: "",
        selected: [],
      },
      rightEye: {
        result: "",
        selected: [],
      },
    });

    setFixFollow({
      findings: ["Failure to Fix", "Failure to Follow"],
      leftEye: {
        result: "",
        selected: [],
      },
      rightEye: {
        result: "",
        selected: [],
      },
    });

    setCornealLightReflex({
      findings: ["Asymmetric", "Displaced"],
      leftEye: {
        result: "",
        selected: [],
      },
      rightEye: {
        result: "",
        selected: [],
      },
    });

    setCoverTest({
      findings: ["Refixation Movement Present"],
      leftEye: {
        result: "",
        selected: [],
      },
      rightEye: {
        result: "",
        selected: [],
      },
    });

    setPs({
      "Refraction – DS": {
        "OD (Right Eye)": "",
        "OS (Left Eye)": "",
      },
      "Refraction – DC": {
        "OD (Right Eye)": "",
        "OS (Left Eye)": "",
      },
      Axis: {
        "OD (Right Eye)": "",
        "OS (Left Eye)": "",
      },
      "Spherical Equivalent": {
        "OD (Right Eye)": "",
        "OS (Left Eye)": "",
      },
      "Gaze Symmetry": {
        "OD (Right Eye)": "",
        "OS (Left Eye)": "",
      },
      "Pupil Diameter": {
        "OD (Right Eye)": "",
        "OS (Left Eye)": "",
      },
      "Pupillary Distance": {
        "OD (Right Eye)": "",
        "OS (Left Eye)": "",
      },
    });

    setDx({
      Hyperopia: {
        "OD (Right Eye)": "",
        "OS (Left Eye)": "",
      },
      Myopia: {
        "OD (Right Eye)": "",
        "OS (Left Eye)": "",
      },
      Anisometropia: {
        "OD (Right Eye)": "",
        "OS (Left Eye)": "",
      },
      Astigmatism: {
        "OD (Right Eye)": "",
        "OS (Left Eye)": "",
      },
      Strabismus: {
        "OD (Right Eye)": "",
        "OS (Left Eye)": "",
      },
      Anisocoria: {
        "OD (Right Eye)": "",
        "OS (Left Eye)": "",
      },
    });

    setReport(null);
    setUploadedUrls([]);
    setRecommendation("");
    setFinalRecommendation("");
  };

  /* ---------------------------------------------------------
      LOAD EXISTING VISION SCREENING
  ---------------------------------------------------------- */

  const loadExistingVision = async () => {
    if (!emrId) return;

    try {
      const res = await api.dataCollectionApi.get(
        `/api/forms/vision/${encodeURIComponent(emrId)}`,
      );

      const saved = res.data?.data;

      if (!saved) {
        resetForm();
        return;
      }

      const savedObservational = saved["Initial Observational Eye Exams"] ?? {};

      const savedPhotoScreener = saved["Photo-Screener Data Entry"] ?? {};

      const savedPdf = saved["Upload PDF Report from Photo-Screener"] ?? [];

      const savedRecommendation = saved["Optometrist Recommendation"] ?? "";

      const savedFinalRecommendation =
        saved.finalRecommendation ?? saved["Final Recommendation"] ?? "";

      const restoreExam = (
        source: any,
        currentFindings: string[],
      ): ObservExam => ({
        findings: currentFindings,
        leftEye: {
          result:
            source?.["Left Eye"]?.["Finding Present"] === "Yes"
              ? "yes"
              : source?.["Left Eye"]?.["Finding Present"] === "No"
                ? "no"
                : "",
          selected: source?.["Left Eye"]?.Findings ?? [],
        },
        rightEye: {
          result:
            source?.["Right Eye"]?.["Finding Present"] === "Yes"
              ? "yes"
              : source?.["Right Eye"]?.["Finding Present"] === "No"
                ? "no"
                : "",
          selected: source?.["Right Eye"]?.Findings ?? [],
        },
      });

      setRedReflex(
        restoreExam(savedObservational["Red Reflex Test"], [
          "Absent",
          "White",
          "Dull",
          "Opacified",
          "Asymmetric",
        ]),
      );

      setExternalInspection(
        restoreExam(savedObservational["External Inspection"], [
          "Ptosis",
          "Epiphora",
        ]),
      );

      setPupillaryExam(
        restoreExam(savedObservational["Pupillary Examination"], [
          "Irregular Shape",
          "Unequal Size",
          "Poor Reaction to Light",
          "Unequal Reaction to Light",
        ]),
      );

      setFixFollow(
        restoreExam(savedObservational["Fix & Follow"], [
          "Failure to Fix",
          "Failure to Follow",
        ]),
      );

      setCornealLightReflex(
        restoreExam(savedObservational["Corneal Light Reflex"], [
          "Asymmetric",
          "Displaced",
        ]),
      );

      setCoverTest(
        restoreExam(savedObservational["Cover Test"], [
          "Refixation Movement Present",
        ]),
      );

      const restoredPs: Record<string, Record<EyeSide, string>> = {
        "Refraction – DS": {
          "OD (Right Eye)": "",
          "OS (Left Eye)": "",
        },
        "Refraction – DC": {
          "OD (Right Eye)": "",
          "OS (Left Eye)": "",
        },
        Axis: {
          "OD (Right Eye)": "",
          "OS (Left Eye)": "",
        },
        "Spherical Equivalent": {
          "OD (Right Eye)": "",
          "OS (Left Eye)": "",
        },
        "Gaze Symmetry": {
          "OD (Right Eye)": "",
          "OS (Left Eye)": "",
        },
        "Pupil Diameter": {
          "OD (Right Eye)": "",
          "OS (Left Eye)": "",
        },
        "Pupillary Distance": {
          "OD (Right Eye)": "",
          "OS (Left Eye)": "",
        },
      };

      (["OD (Right Eye)", "OS (Left Eye)"] as EyeSide[]).forEach((eye) => {
        Object.keys(restoredPs).forEach((parameter) => {
          const value = savedPhotoScreener?.[eye]?.[parameter];

          restoredPs[parameter][eye] =
            value !== undefined && value !== null ? String(value) : "";
        });
      });

      setPs(restoredPs);

      const restoredDx: Record<DxNames, Record<EyeSide, Outcome>> = {
        Hyperopia: {
          "OD (Right Eye)": "",
          "OS (Left Eye)": "",
        },
        Myopia: {
          "OD (Right Eye)": "",
          "OS (Left Eye)": "",
        },
        Anisometropia: {
          "OD (Right Eye)": "",
          "OS (Left Eye)": "",
        },
        Astigmatism: {
          "OD (Right Eye)": "",
          "OS (Left Eye)": "",
        },
        Strabismus: {
          "OD (Right Eye)": "",
          "OS (Left Eye)": "",
        },
        Anisocoria: {
          "OD (Right Eye)": "",
          "OS (Left Eye)": "",
        },
      };

      (Object.keys(restoredDx) as DxNames[]).forEach((diagnosis) => {
        (["OD (Right Eye)", "OS (Left Eye)"] as EyeSide[]).forEach((eye) => {
          const value = savedPhotoScreener?.[eye]?.[diagnosis];

          restoredDx[diagnosis][eye] =
            value === "Pass" ? "pass" : value === "Fail" ? "fail" : "";
        });
      });

      setDx(restoredDx);

      const pdfKey = Array.isArray(savedPdf) ? (savedPdf[0] ?? "") : "";

      setUploadedUrls(pdfKey ? [pdfKey] : []);

      setReport(null);

      setRecommendation(
        savedRecommendation === "Pass"
          ? "Pass"
          : savedRecommendation === "Refer"
            ? "Refer"
            : "",
      );

      setFinalRecommendation(savedFinalRecommendation);
    } catch (err: any) {
      if (err?.response?.status === 404) {
        resetForm();
        return;
      }

      console.error("Failed to load existing Vision Screening:", err);
    }
  };

  useEffect(() => {
    loadExistingVision();
  }, [emrId]);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // =========================
    // Validation
    // =========================

    // Initial Observational Eye Exams

    if (redReflex.leftEye.result === "" || redReflex.rightEye.result === "") {
      alert("Please select Red Reflex Test");
      return;
    }

    if (
      externalInspection.leftEye.result === "" ||
      externalInspection.rightEye.result === ""
    ) {
      alert("Please select External Inspection");
      return;
    }

    if (
      pupillaryExam.leftEye.result === "" ||
      pupillaryExam.rightEye.result === ""
    ) {
      alert("Please select Pupillary Examination");
      return;
    }

    if (fixFollow.leftEye.result === "" || fixFollow.rightEye.result === "") {
      alert("Please select Fix & Follow");
      return;
    }

    if (
      cornealLightReflex.leftEye.result === "" ||
      cornealLightReflex.rightEye.result === ""
    ) {
      alert("Please select Corneal Light Reflex");
      return;
    }

    if (coverTest.leftEye.result === "" || coverTest.rightEye.result === "") {
      alert("Please select Cover Test");
      return;
    }

    for (const parameter of Object.keys(ps)) {
      if (ps[parameter]["OD (Right Eye)"] === "") {
        alert(`Please enter OD value for ${parameter}`);
        return;
      }

      if (ps[parameter]["OS (Left Eye)"] === "") {
        alert(`Please enter OS value for ${parameter}`);
        return;
      }
    }

    for (const diagnosis of Object.keys(dx)) {
      if (dx[diagnosis as DxNames]["OD (Right Eye)"] === "") {
        alert(`Please select OD result for ${diagnosis}`);
        return;
      }

      if (dx[diagnosis as DxNames]["OS (Left Eye)"] === "") {
        alert(`Please select OS result for ${diagnosis}`);
        return;
      }
    }

    if (!report && uploadedUrls.length === 0) {
      alert("Please upload PDF Report");
      return;
    }

    if (recommendation === "") {
      alert("Please select Optometrist Recommendation");
      return;
    }

    const photoScreenerData: Record<EyeSide, any> = {
      "OD (Right Eye)": {},
      "OS (Left Eye)": {},
    };

    Object.keys(ps).forEach((param) => {
      (["OD (Right Eye)", "OS (Left Eye)"] as EyeSide[]).forEach((eye) => {
        photoScreenerData[eye][param] = ps[param][eye]
          ? Number(ps[param][eye])
          : undefined;
      });
    });

    Object.keys(dx).forEach((disease) => {
      (["OD (Right Eye)", "OS (Left Eye)"] as EyeSide[]).forEach((eye) => {
        const val = dx[disease as DxNames][eye];
        if (val) {
          photoScreenerData[eye][disease] = val === "pass" ? "Pass" : "Fail";
        }
      });
    });

    // onComplete && onComplete();

    const buildEyePayload = (exam: ObservExam) => ({
      "Left Eye":
        exam.leftEye.result === "yes"
          ? {
              "Finding Present": "Yes",
              Findings: exam.leftEye.selected,
            }
          : {
              "Finding Present": "No",
            },

      "Right Eye":
        exam.rightEye.result === "yes"
          ? {
              "Finding Present": "Yes",
              Findings: exam.rightEye.selected,
            }
          : {
              "Finding Present": "No",
            },
    });

    const payload = {
      emrId,

      "Initial Observational Eye Exams": {
        "Red Reflex Test": buildEyePayload(redReflex),

        "External Inspection": buildEyePayload(externalInspection),

        "Pupillary Examination": buildEyePayload(pupillaryExam),

        "Fix & Follow": buildEyePayload(fixFollow),

        "Corneal Light Reflex": buildEyePayload(cornealLightReflex),

        "Cover Test": buildEyePayload(coverTest),
      },

      "Photo-Screener Data Entry": photoScreenerData,

      // ⭐ FIXED — now sends S3 URLs to Mongo
      "Upload PDF Report from Photo-Screener": uploadedUrls,

      "Optometrist Recommendation": recommendation || null,

      finalRecommendation: finalRecommendation || "",
    };

    try {
      const res = await api.dataCollectionApi.post(
        "/api/forms/vision",
        payload,
      );
      alert("Vision form submitted successfully!");
      console.log("✅ Vision POST success:", res.data);

      resetForm();

      onComplete && onComplete();
    } catch (error: any) {
      console.error("❌ Vision POST failed:", error);
      alert("Failed to submit vision form");
    }
  };

  const YesNoRadios = ({
    value,
    onChange,
    name,
  }: {
    value: YesNo;
    onChange: (v: YesNo) => void;
    name: string;
  }) => (
    <div className="vs-yn">
      <label>
        <input
          type="radio"
          name={name}
          checked={value === "yes"}
          onChange={() => onChange("yes")}
        />
        Yes
      </label>
      <label>
        <input
          type="radio"
          name={name}
          checked={value === "no"}
          onChange={() => onChange("no")}
        />
        No
      </label>
    </div>
  );

  const DxRow = ({ label, keyName }: { label: string; keyName: DxNames }) => (
    <div className="vs-dx-row">
      <div className="vs-dx-label">{label}</div>
      <div className="vs-dx-radios">
        {(["OD (Right Eye)", "OS (Left Eye)"] as EyeSide[]).map((eye) => (
          <div className="vs-dx-eye" key={eye}>
            <span className="vs-dx-eye-title">{eye}</span>
            <label>
              <input
                type="radio"
                name={`${keyName}-${eye}`}
                checked={dx[keyName][eye] === "pass"}
                onChange={setDxField(keyName, eye, "pass")}
              />
              Pass
            </label>
            <label>
              <input
                type="radio"
                name={`${keyName}-${eye}`}
                checked={dx[keyName][eye] === "fail"}
                onChange={setDxField(keyName, eye, "fail")}
              />
              Fail
            </label>
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <section className="vs-main">
      <div className="vs-section-bar">Vision Screening</div>

      <form className="vs-form" onSubmit={onSubmit}>
        {/* Initial Observational Eye Exams */}
        <div className="vs-card">
          {/* <div className="vs-card-title">Initial Observational Eye Exams</div> */}
          <div
            className="vs-card-title vitals-collapsible"
            onClick={() => toggleSection("observational")}
          >
            <span>Initial Observational Eye Exams</span>

            <span className="collapse-icon">
              {expandedSections.observational ? "▼" : "▶"}
            </span>
          </div>

          {expandedSections.observational && (
            <div className="vs-obs-grid">
              {(
                [
                  {
                    title: "Red Reflex Test",
                    subtitle: "Direct Ophthalmoscope",
                    state: redReflex,
                    setter: setRedReflex,
                  },
                  {
                    title: "External Inspection",
                    subtitle: "NA",
                    note: "Structural Abnormality (Ex: Ptosis, Epiphora)",
                    state: externalInspection,
                    setter: setExternalInspection,
                  },
                  {
                    title: "Pupillary Examination",
                    subtitle: "Torchlight Examination",
                    note: "Irregular shape, Unequal Size, or Poor or unequal reaction to light",
                    state: pupillaryExam,
                    setter: setPupillaryExam,
                  },
                  {
                    title: "Fix & Follow",
                    subtitle: "Torchlight Examination",
                    note: "Failure to fix and follow",
                    state: fixFollow,
                    setter: setFixFollow,
                  },
                  {
                    title: "Corneal Light Reflex",
                    subtitle: "Torchlight Examination",
                    note: "Asymmetric or displaced",
                    state: cornealLightReflex,
                    setter: setCornealLightReflex,
                  },
                  {
                    title: "Cover Test",
                    subtitle: "Occluder",
                    note: "Refixation movement",
                    state: coverTest,
                    setter: setCoverTest,
                  },
                ] as const
              ).map(({ title, subtitle, state, setter }, idx) => (
                <div
                  className={`vs-obs-item ${
                    state.leftEye.result !== "" || state.rightEye.result !== ""
                      ? "selected"
                      : ""
                  }`}
                  key={idx}
                >
                  <div className="vs-obs-title">{title}</div>

                  <div className="vs-obs-section">
                    <div className="vs-label">Instrument</div>
                    <div className="vs-value">{subtitle}</div>
                  </div>

                  <hr />

                  {/* LEFT EYE */}
                  <div className="vs-eye-section">
                    <div className="vs-eye-title">Left Eye</div>

                    <div className="vs-yn">
                      <label>
                        <input
                          type="radio"
                          name={`${title}-left`}
                          checked={state.leftEye.result === "yes"}
                          onChange={() =>
                            setter((prev) => ({
                              ...prev,
                              leftEye: {
                                ...prev.leftEye,
                                result: "yes",
                              },
                            }))
                          }
                        />
                        Yes
                      </label>

                      <label>
                        <input
                          type="radio"
                          name={`${title}-left`}
                          checked={state.leftEye.result === "no"}
                          onChange={() =>
                            setter((prev) => ({
                              ...prev,
                              leftEye: {
                                ...prev.leftEye,
                                result: "no",
                                selected: [],
                              },
                            }))
                          }
                        />
                        No
                      </label>
                    </div>

                    {state.leftEye.result === "yes" && (
                      <div className="vs-findings">
                        <div className="vs-label">Finding</div>

                        {state.findings.map((finding) => (
                          <label
                            key={`left-${finding}`}
                            className="vs-finding-item"
                          >
                            <input
                              type="checkbox"
                              checked={state.leftEye.selected.includes(finding)}
                              onChange={(e) =>
                                setter((prev) => ({
                                  ...prev,
                                  leftEye: {
                                    ...prev.leftEye,
                                    selected: e.target.checked
                                      ? [...prev.leftEye.selected, finding]
                                      : prev.leftEye.selected.filter(
                                          (item) => item !== finding,
                                        ),
                                  },
                                }))
                              }
                            />
                            {finding}
                          </label>
                        ))}
                      </div>
                    )}
                  </div>

                  <hr />

                  {/* RIGHT EYE */}
                  <div className="vs-eye-section">
                    <div className="vs-eye-title">Right Eye</div>

                    <div className="vs-yn">
                      <label>
                        <input
                          type="radio"
                          name={`${title}-right`}
                          checked={state.rightEye.result === "yes"}
                          onChange={() =>
                            setter((prev) => ({
                              ...prev,
                              rightEye: {
                                ...prev.rightEye,
                                result: "yes",
                              },
                            }))
                          }
                        />
                        Yes
                      </label>

                      <label>
                        <input
                          type="radio"
                          name={`${title}-right`}
                          checked={state.rightEye.result === "no"}
                          onChange={() =>
                            setter((prev) => ({
                              ...prev,
                              rightEye: {
                                ...prev.rightEye,
                                result: "no",
                                selected: [],
                              },
                            }))
                          }
                        />
                        No
                      </label>
                    </div>

                    {state.rightEye.result === "yes" && (
                      <div className="vs-findings">
                        <div className="vs-label">Finding</div>

                        {state.findings.map((finding) => (
                          <label
                            key={`right-${finding}`}
                            className="vs-finding-item"
                          >
                            <input
                              type="checkbox"
                              checked={state.rightEye.selected.includes(
                                finding,
                              )}
                              onChange={(e) =>
                                setter((prev) => ({
                                  ...prev,
                                  rightEye: {
                                    ...prev.rightEye,
                                    selected: e.target.checked
                                      ? [...prev.rightEye.selected, finding]
                                      : prev.rightEye.selected.filter(
                                          (item) => item !== finding,
                                        ),
                                  },
                                }))
                              }
                            />
                            {finding}
                          </label>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Photo Screener */}
        <div className="vs-card">
          {/* <div className="vs-card-title">Photo-Screener Data Entry</div> */}
          <div
            className="vs-card-title vitals-collapsible"
            onClick={() => toggleSection("photoScreener")}
          >
            <span>Photo-Screener Data Entry</span>

            <span className="collapse-icon">
              {expandedSections.photoScreener ? "▼" : "▶"}
            </span>
          </div>

          {/* Header row */}
          {expandedSections.photoScreener && (
            <>
              <div className="vs-ps-header">
                <span className="vs-ps-col-param">Parameter</span>
                <span className="vs-ps-col-eye">OD (Right Eye)</span>
                <span className="vs-ps-col-eye">OS (Left Eye)</span>
              </div>

              <div className="vs-ps-table">
                {Object.keys(ps).map((param) => {
                  const getUnit = (label: string) => {
                    if (label.includes("Spherical Equivalent")) return "(dpt)";
                    if (label.includes("Gaze Symmetry")) return "(°)";
                    if (
                      label.includes("Pupil Diameter") ||
                      label.includes("Pupillary Distance")
                    )
                      return "(mm)";
                    return "";
                  };

                  return (
                    <div key={param} className="vs-ps-row">
                      <label className="vs-ps-label">{param}</label>
                      {(["OD (Right Eye)", "OS (Left Eye)"] as EyeSide[]).map(
                        (eye) => (
                          <div className="vs-ps-input-wrapper" key={eye}>
                            <input
                              type="number"
                              className="vs-ps-input"
                              placeholder={eye}
                              value={ps[param][eye]}
                              onChange={setPsField(param, eye)}
                            />
                            {getUnit(param) && (
                              <span className="vs-ps-unit">
                                {getUnit(param)}
                              </span>
                            )}
                          </div>
                        ),
                      )}
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>

        {/* Diagnoses */}
        <div className="vs-card">
          {/* <div className="vs-card-title">Diagnoses</div> */}
          <div
            className="vs-card-title vitals-collapsible"
            onClick={() => toggleSection("diagnoses")}
          >
            <span>Diagnoses</span>

            <span className="collapse-icon">
              {expandedSections.diagnoses ? "▼" : "▶"}
            </span>
          </div>

          {/* Header */}
          {expandedSections.diagnoses && (
            <>
              <div className="vs-dx-header">
                <span className="vs-dx-col-param"></span>
                <span className="vs-dx-col-eye">OD (Right Eye)</span>
                <span className="vs-dx-col-eye">OS (Left Eye)</span>
              </div>

              <div className="vs-dx-table">
                {Object.keys(dx).map((keyName) => (
                  <div key={keyName} className="vs-dx-row">
                    <span className="vs-dx-label">{keyName}</span>

                    {(["OD (Right Eye)", "OS (Left Eye)"] as EyeSide[]).map(
                      (eye) => (
                        <div className="vs-dx-options" key={eye}>
                          <label className="vs-dx-radio">
                            <input
                              type="radio"
                              name={`${keyName}-${eye}`}
                              checked={dx[keyName as DxNames][eye] === "pass"}
                              onChange={setDxField(
                                keyName as DxNames,
                                eye,
                                "pass",
                              )}
                            />
                            Pass
                          </label>
                          <label className="vs-dx-radio">
                            <input
                              type="radio"
                              name={`${keyName}-${eye}`}
                              checked={dx[keyName as DxNames][eye] === "fail"}
                              onChange={setDxField(
                                keyName as DxNames,
                                eye,
                                "fail",
                              )}
                            />
                            Fail
                          </label>
                        </div>
                      ),
                    )}
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        {/* PDF Upload */}
        <div className="vs-card">
          {/* <div className="vs-card-title">
            Upload PDF Report from Photo-Screener
          </div> */}
          <div
            className="vs-card-title vitals-collapsible"
            onClick={() => toggleSection("pdfUpload")}
          >
            <span>Upload PDF Report from Photo-Screener</span>

            <span className="collapse-icon">
              {expandedSections.pdfUpload ? "▼" : "▶"}
            </span>
          </div>

          {expandedSections.pdfUpload && (
            <div className="vs-upload-container">
              <label htmlFor="reportUpload" className="vs-upload-area">
                <img
                  src="/assets/icons/upload_icon.svg"
                  alt="Upload Icon"
                  className="vs-upload-icon"
                />
                <p className="vs-upload-text">Drop your PDF file here or</p>

                <button
                  type="button"
                  className="vs-upload-btn"
                  onClick={() =>
                    document.getElementById("reportUpload")?.click()
                  }
                >
                  Click to Upload
                </button>

                <small className="vs-upload-hint">
                  Maximum file size: 10MB
                </small>

                {uploading && (
                  <p style={{ textAlign: "center", color: "#0a7" }}>
                    Uploading… {progress}%
                  </p>
                )}

                {report && (
                  <div className="vs-uploaded-file">
                    <div className="vs-uploaded-file-name">
                      📄 {report.name}
                    </div>

                    <button
                      type="button"
                      className="vs-remove-file-btn"
                      onClick={removeReport}
                    >
                      ✕
                    </button>
                  </div>
                )}

                {!report && uploadedUrls.length > 0 && (
                  <div className="vs-uploaded-file">
                    <div className="vs-uploaded-file-name">
                      📄{" "}
                      {uploadedUrls[0]?.split("/").pop() ??
                        "Previously uploaded PDF"}
                    </div>

                    <button
                      type="button"
                      className="vs-remove-file-btn"
                      onClick={removeReport}
                    >
                      ✕
                    </button>
                  </div>
                )}

                <input
                  id="reportUpload"
                  type="file"
                  accept=".pdf"
                  onChange={onPickReport}
                  hidden
                />
              </label>
            </div>
          )}
        </div>

        {/* Recommendation */}
        <div className="vs-card">
          {/* <div className="vs-card-title">Optometrist Recommendation</div> */}
          <div
            className="vs-card-title vitals-collapsible"
            onClick={() => toggleSection("recommendation")}
          >
            <span>Optometrist Recommendation</span>

            <span className="collapse-icon">
              {expandedSections.recommendation ? "▼" : "▶"}
            </span>
          </div>

          {expandedSections.recommendation && (
            <>
              <div className="vs-rec-row">
                <label className="vs-rec-option">
                  <input
                    type="radio"
                    name="recommend"
                    value="Pass"
                    checked={recommendation === "Pass"}
                    onChange={() => setRecommendation("Pass")}
                  />
                  Pass
                </label>
                <label className="vs-rec-option">
                  <input
                    type="radio"
                    name="recommend"
                    value="Refer"
                    checked={recommendation === "Refer"}
                    onChange={() => setRecommendation("Refer")}
                  />
                  Refer
                </label>
              </div>
              <p className="vs-rec-note">
                Protocol: If the child fails any test, then choose “Refer”, else
                choose “Pass”.
              </p>

              <div className="vs-final-recommendation">
                <label htmlFor="finalRecommendation">
                  Final Recommendation
                </label>

                <textarea
                  id="finalRecommendation"
                  className="vs-final-recommendation-input"
                  value={finalRecommendation}
                  onChange={(e) => setFinalRecommendation(e.target.value)}
                  placeholder="Enter final recommendation"
                  rows={4}
                />
              </div>
            </>
          )}
        </div>

        {/* bottom buttons */}
        <div className="audio-actions">
          <button type="button" className="audio-btn audio-btn-secondary">
            Close
          </button>

          <button type="submit" className="audio-btn audio-btn-primary">
            Submit Report
          </button>
        </div>
      </form>
    </section>
  );
};
